'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import type { CompanySuggestionsView } from '@galapagos/shared';
import {
  Autocomplete,
  AutocompleteEmpty,
  Button,
  Callout,
  ExpandableCallout,
  Link,
} from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { showsPlanDetails } from '../health-plan/plan-details';
import {
  EMPLOYER_SEARCH_MIN_LENGTH,
  employerSearchQueryKey,
  useEmployerSearch,
} from '../hooks/useEmployerSearch';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { signupSponsorQuery } from '../hooks/useSignupSponsor';
import { useStepHeading } from '../hooks/useStepHeading';
import { startQuietEligibilityCheck } from '../profile/eligibility-poller';
import { buildEligibilityRequest } from '../profile/profile-answers';
import { useSignupLegalName } from '../profile/signup-identity';
import { readWithFallback } from '../services/read-diagnostics';
import { trackRegFlowEmployerSearched } from '../services/regflow-analytics';
import { carrierParentRefFor, FIRST_SIGNUP_STEP } from '../steps';
import { useSignupFlowStore, type SignupLegalName } from '../stores/signup-flow-store';
import { ReturnToStep } from './ReturnToStep';
import { EarlierAnswersGate } from './EarlierAnswersGate';

/** The picked directory entry. `ref` is opaque and goes back exactly as given. */
interface PickedEmployer {
  ref: string;
  name: string;
}

/** "Contact <email> if you can't find…", with the address as a mail link. */
function HelpBody() {
  const { t } = useTranslation('signup');
  const { t: tCommon } = useTranslation('common');
  const email = tCommon('siteFooter.emailAddress');
  return (
    <>
      {t('employerSearch.helpBody.intro')}
      <Link
        label={email}
        href={`mailto:${email}`}
        actionId="signup.employer-search-screen-help-email.open"
        data-testid="signup-employer-help-email"
      />
      {t('employerSearch.helpBody.outro')}
    </>
  );
}

function EmployerSearchStep({
  legalName,
  birthDate,
  parentRef,
  flow,
}: {
  legalName: SignupLegalName;
  birthDate: string;
  parentRef: string | undefined;
  flow: string | undefined;
}) {
  const { t } = useTranslation('signup');
  const api = useSignupApi();
  const setSponsorRef = useSignupFlowStore((state) => state.setSponsorRef);
  const { goToStep } = useSignupNavigation();
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<PickedEmployer | null>(null);
  const [continuing, setContinuing] = useState(false);
  // A ref, not state: a second press in the same frame must see the first.
  const continuingRef = useRef(false);
  // A pick whose sponsor answers after this screen has gone must not move the flow on.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  const title = t('employerSearch.title');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();

  // Once a pick fills the field, its name is not searched again.
  const search = useEmployerSearch(picked ? '' : text, parentRef);
  const searching = !picked && text.trim().length >= EMPLOYER_SEARCH_MIN_LENGTH;
  const failed = searching && search.isError;
  // Every entry is shown and handled the same way: the screen never looks
  // inside a ref or a name, so a decoy entry behaves like any other.
  const suggestions =
    searching && !failed
      ? (search.data?.suggestions ?? []).map((entry) => ({ id: entry.ref, label: entry.name }))
      : [];
  const noResults =
    searching && search.isSuccess && !search.isPlaceholderData && suggestions.length === 0;

  // Production counts a settled empty search once per distinct query on a
  // visit. Until the debounce catches up, the shown result answers the
  // previous query, so only a cached empty answer to exactly the current text
  // counts. The query is a lookup and dedupe key and never leaves the screen.
  const queryClient = useQueryClient();
  const scenario = useSearchParams().get('scenario');
  const reportedEmpty = useRef(new Set<string>());
  useEffect(() => {
    if (!noResults) return;
    const query = text.trim();
    const answer = queryClient.getQueryData<CompanySuggestionsView>(
      employerSearchQueryKey(scenario, parentRef ?? null, query),
    );
    if (answer === undefined || answer.suggestions.length > 0) return;
    const key = query.toLowerCase();
    if (reportedEmpty.current.has(key)) return;
    reportedEmpty.current.add(key);
    trackRegFlowEmployerSearched('no_results');
  }, [noResults, text, queryClient, scenario, parentRef, search.dataUpdatedAt]);

  const onContinue = async () => {
    if (picked === null || continuingRef.current) return;
    continuingRef.current = true;
    setSponsorRef(picked.ref);
    // The first check (name and birth date) found no match; this one names
    // the employer. The member never waits on it.
    startQuietEligibilityCheck(
      api,
      buildEligibilityRequest({ legalName, dateOfBirth: birthDate, clientRef: picked.ref }),
    );
    // A health plan asks for the member's plan details before anything else.
    // The read is the one the next steps make anyway; a failed one carries on
    // without them, as the code step does on an employer link.
    setContinuing(true);
    const epoch = useSignupFlowStore.getState().flowEpoch;
    const sponsor = await readWithFallback('sponsor_after_search', 'skip_plan_details', () =>
      queryClient.fetchQuery(signupSponsorQuery(api, scenario, picked.ref, undefined)),
    );
    if (!liveRef.current) return;
    continuingRef.current = false;
    setContinuing(false);
    if (useSignupFlowStore.getState().flowEpoch !== epoch) return;
    goToStep(
      showsPlanDetails({ healthPlan: sponsor?.features.healthPlan, flow })
        ? 'plan-details'
        : 'substance',
    );
  };

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        <ExpandableCallout
          title={t('employerSearch.helpTitle')}
          actionId="signup.employer-search-screen-help.toggle"
          data-testid="signup-employer-help"
        >
          <HelpBody />
        </ExpandableCallout>
        {/* The one status announcer for this step: always mounted, its text
            changes. The visible error below is hidden from assistive
            technology so the message is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {failed ? t('employerSearch.searchError') : ''}
        </p>
        {/* Free-typed employer names can identify a member, so the field and
            its suggestions stay out of autocapture and replay (ADR-112). */}
        <PrivateFields>
          <Autocomplete
            actionId="signup.employer-search-screen-suggestion.select"
            label={t('employerSearch.searchLabel')}
            helperText={t('employerSearch.searchHelper')}
            suggestionsLabel={t('employerSearch.suggestionsLabel')}
            value={text}
            loading={searching && search.isFetching}
            suggestions={suggestions}
            emptyState={
              noResults ? (
                <AutocompleteEmpty
                  title={t('employerSearch.noResultsTitle')}
                  data-testid="signup-employer-no-results"
                >
                  <HelpBody />
                </AutocompleteEmpty>
              ) : undefined
            }
            onChangeText={(value) => {
              setText(value);
              setPicked(null);
            }}
            onSelectSuggestion={(suggestion) => {
              // The directory lists covered sponsors, so a pick is production's "covered".
              trackRegFlowEmployerSearched('covered');
              setPicked({ ref: suggestion.id, name: suggestion.label });
              setText(suggestion.label);
            }}
            name="employer-search"
            data-testid="signup-employer-search"
          />
        </PrivateFields>
        {failed ? (
          <div aria-hidden="true">
            <Callout tone="error" body={t('employerSearch.searchError')} />
          </div>
        ) : null}
        <PrivacyNote text={t('employerSearch.privacyNote')} />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            state={continuing ? 'Loading' : picked ? 'Default' : 'Disabled'}
            onPress={() => void onContinue()}
            actionId="signup.employer-search-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}

/**
 * `/employer-search/`: on a link with no employer, when the check found
 * no match, the member searches the company directory for their employer or
 * health plan (v9 2.4); on an Express Scripts link, for their carrier. The pick
 * becomes the sponsor for later steps.
 */
export function EmployerSearchStepScreen() {
  const { path } = useSignupNavigation();
  // Every flow page refills the sponsor params from its own URL.
  const params = useLegacySignupParams();
  const legalName = useSignupLegalName();
  const birthDate = useSignupFlowStore((state) => state.birthDate);
  if (path !== 'no-employer') return <ReturnToStep step={FIRST_SIGNUP_STEP} />;
  return (
    <EarlierAnswersGate step="employer-search">
      {legalName !== null && birthDate !== null ? (
        // An Express Scripts link searches its carriers rather than every employer.
        <EmployerSearchStep
          legalName={legalName}
          birthDate={birthDate}
          parentRef={carrierParentRefFor(params)}
          flow={params.flow}
        />
      ) : null}
    </EarlierAnswersGate>
  );
}
